import { LearnMoreLink, Modal, Text } from '@ui/themed';
import { t } from 'i18next';
import z from 'zod';
import type { Setting } from '../../../../context/settings-context';
import { useSettings } from '../../../../context/settings-context';
import type { I18nPrefix } from '../../../../i18n/prefix';
import { useAppForm } from '../../../../lib/app-form';
import { PARTNER_DOCS_URLS } from '../../constants';
import type { PartnerModalProps } from '../../types';
import { CredentialField } from '../credential-field';
import { usePartnerModalSave } from './shared';

const LINNWORKS = 'features:partners.components.linnworks' satisfies I18nPrefix;
const COMMON = 'features:partners.components.common' satisfies I18nPrefix;

export function LinnworksModal({ isOpen, onClose }: PartnerModalProps) {
  const settings = useSettings();
  const { save, isSaving } = usePartnerModalSave(onClose);

  const hasStoredToken = !!settings.integrationLinnworksToken;
  const tokenValidator = hasStoredToken ? z.string() : z.string().trim().min(1);

  const form = useAppForm({
    defaultValues: { token: '' },
    validators: { onSubmit: z.object({ token: tokenValidator }) },
    onSubmit: ({ value }) => {
      const payload: Setting[] = [{ key: 'integrationLinnworks', value: true }];
      const token = value.token.trim();
      if (token) {
        payload.push({ key: 'integrationLinnworksToken', value: token });
      }
      void save(payload);
    },
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      secondaryAction={{ text: t(`${COMMON}.close`), onClick: onClose }}
      primaryAction={{
        text: t(`${COMMON}.save`),
        onClick: () => void form.handleSubmit(),
        loading: isSaving,
      }}
    >
      <Modal.Header>Linnworks</Modal.Header>
      <Modal.Body>
        <div className="flex flex-col gap-4">
          <Text variant="secondary">
            {t(`${LINNWORKS}.description`)} <LearnMoreLink link={PARTNER_DOCS_URLS.linnworks} />
          </Text>
          <form.Field name="token">
            {(field) => (
              <CredentialField
                label={t(`${LINNWORKS}.token_label`)}
                value={field.state.value}
                onChange={field.handleChange}
                hasStoredValue={hasStoredToken}
                error={field.state.meta.errors[0]?.message}
                type="password"
              />
            )}
          </form.Field>
        </div>
      </Modal.Body>
    </Modal>
  );
}
